import { useState } from 'react'
import { toast } from 'sonner'
import { trpc } from '@/config/trpc'
import { Button } from '@/components/ui/shadcn/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/shadcn/dialog'
import { Check, Copy, Link2, RefreshCw, Trash2 } from 'lucide-react'

const buildShareUrl = (token: string) => `${window.location.origin}/share/${token}/my-posts`

const formatRelative = (iso: string | Date | null | undefined) => {
  if (!iso) return null
  const date = typeof iso === 'string' ? new Date(iso) : iso
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function ShareLinkDialog() {
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const [confirmingRegenerate, setConfirmingRegenerate] = useState(false)
  const [confirmingRevoke, setConfirmingRevoke] = useState(false)

  const utils = trpc.useUtils()
  const link = trpc.shareLink.get.useQuery(undefined, { enabled: open })

  const reset = () => {
    setConfirmingRegenerate(false)
    setConfirmingRevoke(false)
    setCopied(false)
  }

  const create = trpc.shareLink.create.useMutation({
    onSuccess: () => {
      utils.shareLink.get.invalidate()
    },
    onError: (err) => toast.error(err.message),
  })

  const regenerate = trpc.shareLink.regenerate.useMutation({
    onSuccess: () => {
      utils.shareLink.get.invalidate()
      reset()
      toast.success('Share link regenerated. The old link is no longer valid.')
    },
    onError: (err) => toast.error(err.message),
  })

  const revoke = trpc.shareLink.revoke.useMutation({
    onSuccess: () => {
      utils.shareLink.get.invalidate()
      reset()
      toast.success('Share link revoked.')
    },
    onError: (err) => toast.error(err.message),
  })

  const handleCopy = async (url: string) => {
    await navigator.clipboard.writeText(url)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v)
        if (!v) reset()
      }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Link2 size={13} />
          Share
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Share My Posts</DialogTitle>
          <DialogDescription>
            Anyone with this link can view this brand's posts. Read-only, no login required.
          </DialogDescription>
        </DialogHeader>

        {link.isLoading ? (
          <div className="flex items-center justify-center py-8">
            <span className="border-primary/30 border-t-primary h-5 w-5 animate-spin rounded-full border-2" />
          </div>
        ) : link.data ? (
          <div className="flex min-w-0 flex-col gap-4">
            <div>
              <label className="text-muted-foreground mb-1.5 block text-xs font-medium uppercase tracking-wider">
                Link
              </label>
              <div className="border-border bg-muted flex items-center gap-2 rounded-md border p-2">
                <code className="min-w-0 flex-1 truncate text-xs">
                  {buildShareUrl(link.data.token)}
                </code>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => handleCopy(buildShareUrl(link.data!.token))}>
                  {copied ? (
                    <>
                      <Check size={13} /> Copied
                    </>
                  ) : (
                    <>
                      <Copy size={13} /> Copy
                    </>
                  )}
                </Button>
              </div>
            </div>

            <div className="text-muted-foreground flex flex-col gap-1 text-xs">
              <div>Created {formatRelative(link.data.createdAt)}</div>
              <div>
                {link.data.lastUsedAt
                  ? `Last viewed ${formatRelative(link.data.lastUsedAt)}`
                  : 'Not yet viewed'}
              </div>
            </div>

            <div className="flex flex-col gap-2 pt-2">
              {confirmingRegenerate ? (
                <div className="border-border flex flex-col gap-2 rounded-md border p-3">
                  <p className="text-sm">
                    Regenerating will create a new link and immediately invalidate the existing one.
                    Anyone using the old link will lose access.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setConfirmingRegenerate(false)}>
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => regenerate.mutate()}
                      disabled={regenerate.isPending}>
                      <RefreshCw size={13} />
                      {regenerate.isPending ? 'Regenerating...' : 'Regenerate link'}
                    </Button>
                  </div>
                </div>
              ) : confirmingRevoke ? (
                <div className="border-border flex flex-col gap-2 rounded-md border p-3">
                  <p className="text-sm">
                    Revoking will permanently invalidate this link. Anyone using it will lose
                    access.
                  </p>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setConfirmingRevoke(false)}>
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => revoke.mutate()}
                      disabled={revoke.isPending}
                      className="bg-destructive hover:bg-destructive/90 text-white">
                      <Trash2 size={13} />
                      {revoke.isPending ? 'Revoking...' : 'Revoke link'}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setConfirmingRegenerate(true)}>
                    <RefreshCw size={13} />
                    Regenerate
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setConfirmingRevoke(true)}>
                    <Trash2 size={13} />
                    Revoke
                  </Button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4 py-2">
            <p className="text-muted-foreground text-sm">
              No share link yet. Generate one to send to a client or collaborator.
            </p>
            <DialogFooter>
              <Button onClick={() => create.mutate()} disabled={create.isPending}>
                <Link2 size={13} />
                {create.isPending ? 'Generating...' : 'Generate share link'}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
